import { Node, Edge } from 'reactflow';
import { WorkflowCanvasMode } from '../types';
import { createEdgesFromConnections } from './workflowNodeEdgeUtils';
import { calculateCriticalPath, countDependencies } from './workflowLayout';
import { logNodePosition, validatePosition, logAllNodePositions } from '../../../utils/positionDebugUtils';

/**
 * Validates and normalizes a position object
 * @param position The position to validate
 * @param nodeId The ID of the node (for logging)
 * @param source The source of the position (for logging)
 * @returns A valid position object with numeric x and y values
 */
export function validateAndNormalizePosition(
  position: any, 
  nodeId: string, 
  source: string
): { x: number, y: number } {
  // Simple validation - if position is missing or invalid, use default
  if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
    // Convert to number if possible
    if (position && position.x !== undefined && position.y !== undefined) {
      const x = Number(position.x);
      const y = Number(position.y);
      
      if (!isNaN(x) && !isNaN(y)) {
        return { x, y };
      }
    }
    
    // Use default position if invalid
    return { 
      x: 100 + Math.random() * 500, 
      y: 100 + Math.random() * 300 
    };
  }
  
  // Position is valid, return as is
  return { x: position.x, y: position.y };
}

/**
 * Process workflow data into nodes and edges for ReactFlow
 */
export function processWorkflowData(
  workflowData: any,
  executionData: any,
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void,
  onToggleExpand?: (nodeId: string, isExpanded?: boolean) => void
): { nodes: Node[], edges: Edge[] } {
  console.log(`🔄 PROCESSING WORKFLOW DATA START for mode ${mode}`);
  
  if (!workflowData || !workflowData.phases || !workflowData.connections) {
    console.warn('Incomplete workflow data received, missing phases or connections:', workflowData);
    return { nodes: [], edges: [] };
  }
  
  // Log all phase positions with validation
  if (workflowData.phases) {
    console.log("📍 WORKFLOW PHASE POSITIONS:", 
      JSON.stringify(workflowData.phases.map(phase => ({
        id: phase.id,
        position: phase.position,
        valid: validatePosition(phase.position, phase.id, 'WORKFLOW_DATA')
      })), null, 2)
    );
  }
  
  // Calculate critical path if needed
  const criticalPath = calculateCriticalPath(workflowData);
  
  // Create all nodes
  const allNodes: Node[] = [];

  // Build a quick lookup: personaId → personaName from persona nodes in workflowData.nodes
  const personaNameById: Record<string, string> = {};
  (workflowData.nodes ?? []).forEach((n: any) => {
    if (n.type === 'persona' && n.data?.personaId && n.data?.personaName) {
      personaNameById[n.data.personaId] = n.data.personaName;
    }
  });

  // Transform phases into nodes
  const phaseNodes: Node[] = workflowData.phases.map(phase => {
    // Ensure position is valid and log in a standardized format
    let position;
    if (phase.position && 
        (typeof phase.position.x === 'number' || typeof phase.position.x === 'string') && 
        (typeof phase.position.y === 'number' || typeof phase.position.y === 'string')) {
      // Create a deep copy to avoid reference issues and ensure numbers
      position = { 
        x: Number(phase.position.x), 
        y: Number(phase.position.y) 
      };
      logNodePosition('PROCESS_WORKFLOW_DATA', phase.id, position);
      validatePosition(position, phase.id, 'PROCESS_WORKFLOW_DATA');
    } else {
      // Generate a random position if invalid
      position = { x: 100 + Math.random() * 500, y: 100 + Math.random() * 500 };
      console.warn(`⚠️ WORKFLOW PHASE[${phase.id}] INVALID POSITION - USING RANDOM: x=${position.x}, y=${position.y}`);
      console.warn(`⚠️ ORIGINAL INVALID POSITION:`, JSON.stringify(phase.position, null, 2));
    }
    
    // Merge execution data if available
    let executionPhase = null;
    if (executionData && executionData.phases) {
      executionPhase = executionData.phases.find(p => p.id === phase.id);
    }

    return {
      id: phase.id,
      type: 'phase',
      position: position,
      data: {
        id: phase.id,
        mode,
        label: phase.label || 'Unnamed Phase',
        type: phase.type || 'default',
        duration: phase.config?.duration || 'Unknown',
        onCriticalPath: criticalPath.includes(phase.id),
        dependencyCount: countDependencies(workflowData, phase.id),
        status: executionPhase?.status || phase.status || 'not_started',
        config: {
          ...(phase.config || {}),
          personas: (phase.config?.personas ?? []).map((p: any) => ({
            ...p,
            persona_name: p.persona_name || personaNameById[p.persona_id] || p.persona_id,
          })),
        },
        darkMode,
        execution: executionPhase ? {
          start_time: executionPhase.start_time,
          end_time: executionPhase.end_time,
          artifacts: executionPhase.artifacts || []
        } : null,
        onUpdate: (nodeId, updates) => {
          console.log(`onUpdate called for node ${nodeId} with updates:`, updates);
          if (onWorkflowChange) {
            console.log(`Notifying about change with type: update_phase`);
            onWorkflowChange({
              type: 'update_phase',
              phaseId: nodeId,
              updates
            });
          } else {
            console.warn(`No onWorkflowChange callback available`);
          }
        },
        onToggleExpand: (nodeId, isExpanded) => {
          if (onToggleExpand) {
            onToggleExpand(nodeId, isExpanded);
          }
        }
      }
    };
  });
  
  allNodes.push(...phaseNodes);
  
  // Add tool, persona, and artifact nodes if they exist
  if (workflowData.nodes && Array.isArray(workflowData.nodes)) {
    workflowData.nodes.forEach(node => {
      // Ensure position is valid and properly copied
      let position;
      if (node.position && 
          (typeof node.position.x === 'number' || typeof node.position.x === 'string') && 
          (typeof node.position.y === 'number' || typeof node.position.y === 'string')) {
        // Create a deep copy to avoid reference issues and ensure numbers
        position = { 
          x: Number(node.position.x), 
          y: Number(node.position.y) 
        };
        logNodePosition('PROCESS_WORKFLOW_DATA_ENTITY', node.id, position);
        validatePosition(position, node.id, 'PROCESS_WORKFLOW_DATA_ENTITY');
      } else {
        // Generate a random position if invalid
        position = { x: 100 + Math.random() * 500, y: 100 + Math.random() * 300 };
        console.warn(`⚠️ WORKFLOW ENTITY NODE[${node.id}] INVALID POSITION - USING RANDOM: x=${position.x}, y=${position.y}`);
        console.warn(`⚠️ ORIGINAL INVALID POSITION:`, JSON.stringify(node.position, null, 2));
      }
      
      if (node.type === 'tool') {
        allNodes.push({
          id: node.id,
          type: 'tool',
          position: position,
          data: {
            id: node.id,
            mode,
            darkMode,
            isConnectable: mode === WorkflowCanvasMode.EDIT,
            ...node.data,
            onUpdate: (nodeId, updates) => {
              if (onWorkflowChange) {
                onWorkflowChange({
                  type: 'update_tool',
                  toolNodeId: nodeId,
                  updates
                });
              }
            }
          }
        });
      } else if (node.type === 'persona') {
        allNodes.push({
          id: node.id,
          type: 'persona',
          position: position,
          data: {
            id: node.id,
            mode,
            darkMode,
            isConnectable: mode === WorkflowCanvasMode.EDIT,
            ...node.data,
            onUpdate: (nodeId, updates) => {
              console.log('🔧 PERSONA-NODE-UPDATE: onUpdate called for', nodeId, 'with updates:', updates);
              if (onWorkflowChange) {
                onWorkflowChange({
                  type: 'update_persona',
                  personaNodeId: nodeId,
                  updates
                });
              } else {
                console.error('🔧 PERSONA-NODE-UPDATE: onWorkflowChange is not available!');
              }
            }
          }
        });
      } else if (node.type === 'artifact') {
        allNodes.push({
          id: node.id,
          type: 'artifact',
          position: position,
          data: {
            id: node.id,
            mode,
            darkMode,
            isConnectable: mode === WorkflowCanvasMode.EDIT,
            ...node.data,
            onUpdate: (nodeId, updates) => {
              if (onWorkflowChange) {
                onWorkflowChange({
                  type: 'update_artifact',
                  artifactNodeId: nodeId,
                  updates
                });
              }
            }
          }
        });
      }
    });
  }

  // Create edges from connections
  const edges = createEdgesFromConnections(workflowData.connections, allNodes, criticalPath);
  
  // Log warning if some connections were filtered out
  const filteredConnectionCount = workflowData.connections.length - edges.length;
  if (filteredConnectionCount > 0) {
    console.warn(`Filtered out ${filteredConnectionCount} invalid connections during workflow processing`);
  }
  
  // Log all processed node positions
  logAllNodePositions('PROCESS_WORKFLOW_DATA_RESULT', allNodes);
  
  console.log(`🔄 PROCESSING WORKFLOW DATA COMPLETE`);
  return { nodes: allNodes, edges };
}

/**
 * Update existing nodes with new workflow data
 */
export function updateNodesWithWorkflowData(
  existingNodes: Node[],
  workflowData: any,
  executionData: any,
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void,
  onToggleExpand?: (nodeId: string, isExpanded?: boolean) => void
): Node[] {
  console.log('🔧 UPDATE-NODES-START: Starting updateNodesWithWorkflowData');
  console.log('🔧 UPDATE-NODES-START: Existing nodes count:', existingNodes.length);
  console.log('🔧 UPDATE-NODES-START: First node callbacks:', {
    hasOnUpdate: !!existingNodes[0]?.data?.onUpdate,
    hasOnToggleExpand: !!existingNodes[0]?.data?.onToggleExpand
  });

  if (!workflowData || !workflowData.phases) {
    return existingNodes;
  }
  
  // Build persona name lookup from existing nodes + incoming workflowData.nodes
  const personaNameById: Record<string, string> = {};
  [...existingNodes, ...(workflowData.nodes ?? [])].forEach((n: any) => {
    if (n.type === 'persona' && n.data?.personaId && n.data?.personaName) {
      personaNameById[n.data.personaId] = n.data.personaName;
    }
  });

  // Calculate critical path if needed
  const criticalPath = calculateCriticalPath(workflowData);
  
  // Create a map of existing nodes by ID for quick lookup
  const existingNodesMap = new Map(existingNodes.map(node => [node.id, node]));
  const updatedNodes = [...existingNodes];
  const nodesToAdd: Node[] = [];
  
  // Update phase nodes
  workflowData.phases.forEach(phase => {
    const existingNode = existingNodesMap.get(phase.id);
    
    if (existingNode) {
      // Update existing node data
      const nodeIndex = updatedNodes.findIndex(n => n.id === phase.id);
      if (nodeIndex >= 0) {
        // Merge execution data if available
        let executionPhase = null;
        if (executionData && executionData.phases) {
          executionPhase = executionData.phases.find(p => p.id === phase.id);
        }
        
        // IMPORTANT: Use the position from the workflow data, not the existing node
        // Create a deep copy of the position to avoid reference issues
        const position = phase.position && 
                        typeof phase.position.x !== 'undefined' && 
                        typeof phase.position.y !== 'undefined'
          ? { 
              x: Number(phase.position.x), 
              y: Number(phase.position.y) 
            }
          : existingNode.position;
        
        // Log the position update for debugging
        console.log(`📍 Updating phase ${phase.id} position:`, {
          from: existingNode.position,
          to: position,
          source: 'workflow data'
        });
        
        updatedNodes[nodeIndex] = {
          ...existingNode,
          // Use the updated position
          position: position,
          data: {
            ...existingNode.data,
            label: phase.label || 'Unnamed Phase',
            type: phase.type || 'default',
            config: {
              ...(phase.config || {}),
              personas: (phase.config?.personas ?? []).map((p: any) => ({
                ...p,
                persona_name: p.persona_name || personaNameById[p.persona_id] || p.persona_id,
              })),
            },
            onCriticalPath: criticalPath.includes(phase.id),
            dependencyCount: countDependencies(workflowData, phase.id),
            status: executionPhase?.status || phase.status || existingNode.data.status || 'not_started',
            execution: executionPhase ? {
              start_time: executionPhase.start_time,
              end_time: executionPhase.end_time,
              artifacts: executionPhase.artifacts || []
            } : existingNode.data.execution
          }
        };
      }
    } else {
      // Add new phase node
      let executionPhase = null;
      if (executionData && executionData.phases) {
        executionPhase = executionData.phases.find(p => p.id === phase.id);
      }

      // Ensure we have a valid position and create a copy to avoid reference issues
      const position = phase.position && typeof phase.position.x === 'number' && typeof phase.position.y === 'number'
        ? { ...phase.position }
        : { x: Math.random() * 500, y: Math.random() * 500 };
        
      console.log(`Adding new phase node ${phase.id} with position:`, position);
      
      nodesToAdd.push({
        id: phase.id,
        type: 'phase',
        position: position,
        data: {
          id: phase.id,
          mode,
          label: phase.label || 'Unnamed Phase',
          type: phase.type || 'default',
          duration: phase.config?.duration || 'Unknown',
          onCriticalPath: criticalPath.includes(phase.id),
          dependencyCount: countDependencies(workflowData, phase.id),
          status: executionPhase?.status || phase.status || 'not_started',
          config: {
            ...(phase.config || {}),
            personas: (phase.config?.personas ?? []).map((p: any) => ({
              ...p,
              persona_name: p.persona_name || personaNameById[p.persona_id] || p.persona_id,
            })),
          },
          darkMode,
          execution: executionPhase ? {
            start_time: executionPhase.start_time,
            end_time: executionPhase.end_time,
            artifacts: executionPhase.artifacts || []
          } : null,
          onUpdate: (nodeId, updates) => {
            if (onWorkflowChange) {
              onWorkflowChange({
                type: 'update_phase',
                phaseId: nodeId,
                updates
              });
            }
          },
          onToggleExpand: (nodeId, isExpanded) => {
            if (onToggleExpand) {
              onToggleExpand(nodeId, isExpanded);
            }
          }
        }
      });
    }
  });
  
  // Update entity nodes (tools, personas, artifacts)
  if (workflowData.nodes && Array.isArray(workflowData.nodes)) {
    workflowData.nodes.forEach(node => {
      const existingNode = existingNodesMap.get(node.id);
      
      if (existingNode) {
        // Update existing node data
        const nodeIndex = updatedNodes.findIndex(n => n.id === node.id);
        if (nodeIndex >= 0) {
          // Use server position if available, otherwise keep existing position
          let position;
          if (node.position && typeof node.position.x !== 'undefined' && typeof node.position.y !== 'undefined') {
            // Create a new position object with numeric values
            position = { 
              x: Number(node.position.x), 
              y: Number(node.position.y) 
            };
          } else {
            position = { ...existingNode.position }; // Fallback to existing position
          }
          
          // CRITICAL: Preserve ALL React callbacks before any data merging
          const preservedCallbacks = {
            onUpdate: existingNode.data.onUpdate,
            onToggleExpand: existingNode.data.onToggleExpand,
          };
          
          // CRITICAL FIX: If callbacks are missing, recreate them
          if (!preservedCallbacks.onUpdate && onWorkflowChange) {
            console.log('🔧 CALLBACK-RECREATE: Recreating missing onUpdate callback for', node.id);
            if (node.type === 'persona') {
              preservedCallbacks.onUpdate = (nodeId, updates) => {
                console.log('🔧 PERSONA-NODE-UPDATE: Recreated onUpdate called for', nodeId, 'with updates:', updates);
                onWorkflowChange({
                  type: 'update_persona',
                  personaNodeId: nodeId,
                  updates
                });
              };
            } else if (node.type === 'tool') {
              preservedCallbacks.onUpdate = (nodeId, updates) => {
                onWorkflowChange({
                  type: 'update_tool',
                  toolNodeId: nodeId,
                  updates
                });
              };
            } else if (node.type === 'artifact') {
              preservedCallbacks.onUpdate = (nodeId, updates) => {
                onWorkflowChange({
                  type: 'update_artifact',
                  artifactNodeId: nodeId,
                  updates
                });
              };
            }
          }
          
          console.log('🔧 CALLBACK-PRESERVE: Preserving callbacks for', node.id, {
            hasOnUpdate: !!preservedCallbacks.onUpdate,
            hasOnToggleExpand: !!preservedCallbacks.onToggleExpand,
            onUpdateType: typeof preservedCallbacks.onUpdate
          });
          
          // Create the updated node with explicit callback preservation
          const updatedNodeData = {
            // Start with a clean base from existing data
            id: existingNode.data.id,
            mode: existingNode.data.mode,
            darkMode: existingNode.data.darkMode,
            isConnectable: existingNode.data.isConnectable,
            // Add any other existing non-callback data
            ...(existingNode.data ? Object.fromEntries(
              Object.entries(existingNode.data).filter(([key]) => 
                !key.startsWith('on') && key !== 'onUpdate' && key !== 'onToggleExpand'
              )
            ) : {}),
            // Merge in new non-callback data from workflow
            ...(node.data ? Object.fromEntries(
              Object.entries(node.data).filter(([key]) => 
                !key.startsWith('on') && key !== 'onUpdate' && key !== 'onToggleExpand'
              )
            ) : {}),
            // CRITICAL: Explicitly set callbacks LAST - never allow them to be overwritten
            onUpdate: preservedCallbacks.onUpdate,
            onToggleExpand: preservedCallbacks.onToggleExpand
          };
          
          updatedNodes[nodeIndex] = {
            ...existingNode,
            position: { ...position }, // Force a new object reference
            data: updatedNodeData
          };
          
          console.log('🔧 CALLBACK-RESTORE: Restored callbacks for', node.id, {
            hasOnUpdate: !!updatedNodes[nodeIndex].data.onUpdate,
            hasOnToggleExpand: !!updatedNodes[nodeIndex].data.onToggleExpand,
            finalDataKeys: Object.keys(updatedNodes[nodeIndex].data)
          });
        }
      } else {
        // Add new entity node based on type
        // Ensure position is valid and properly copied
        const position = node.position && 
                        typeof node.position.x === 'number' && 
                        typeof node.position.y === 'number' 
          ? { ...node.position }
          : { x: 100 + Math.random() * 500, y: 100 + Math.random() * 300 };
          
        if (node.type === 'tool') {
          nodesToAdd.push({
            id: node.id,
            type: 'tool',
            position: position,
            data: {
              id: node.id,
              mode,
              darkMode,
              isConnectable: mode === WorkflowCanvasMode.EDIT,
              ...node.data,
              onUpdate: (nodeId, updates) => {
                if (onWorkflowChange) {
                  onWorkflowChange({
                    type: 'update_tool',
                    toolNodeId: nodeId,
                    updates
                  });
                }
              }
            }
          });
        } else if (node.type === 'persona') {
          nodesToAdd.push({
            id: node.id,
            type: 'persona',
            position: position,
            data: {
              id: node.id,
              mode,
              darkMode,
              isConnectable: mode === WorkflowCanvasMode.EDIT,
              ...node.data,
              onUpdate: (nodeId, updates) => {
                console.log('🔧 PERSONA-NODE-UPDATE: onUpdate called for new node', nodeId, 'with updates:', updates);
                if (onWorkflowChange) {
                  onWorkflowChange({
                    type: 'update_persona',
                    personaNodeId: nodeId,
                    updates
                  });
                } else {
                  console.error('🔧 PERSONA-NODE-UPDATE: onWorkflowChange is not available for new node!');
                }
              }
            }
          });
        } else if (node.type === 'artifact') {
          nodesToAdd.push({
            id: node.id,
            type: 'artifact',
            position: position,
            data: {
              id: node.id,
              mode,
              darkMode,
              isConnectable: mode === WorkflowCanvasMode.EDIT,
              ...node.data,
              onUpdate: (nodeId, updates) => {
                if (onWorkflowChange) {
                  onWorkflowChange({
                    type: 'update_artifact',
                    artifactNodeId: nodeId,
                    updates
                  });
                }
              }
            }
          });
        }
      }
    });
  }
  
  // Add any new nodes to the updated nodes array
  if (nodesToAdd.length > 0) {
    updatedNodes.push(...nodesToAdd);
  }

  // ── CRITICAL: Remove nodes that no longer exist in the incoming workflow data ──
  // This catches manually-deleted nodes that the LLM re-introduced, and ghost
  // nodes that survived backend purging.
  const incomingPhaseIds   = new Set((workflowData.phases  ?? []).map((p: any) => p.id));
  const incomingEntityIds  = new Set((workflowData.nodes   ?? []).map((n: any) => n.id));

  const prunedNodes = updatedNodes.filter(node => {
    if (node.type === 'phase') {
      const keep = incomingPhaseIds.has(node.id);
      if (!keep) console.warn(`🧹 PROCESSOR-PRUNE: Removing stale phase node ${node.id}`);
      return keep;
    }
    // tool / persona / artifact — must exist in workflowData.nodes
    const keep = incomingEntityIds.has(node.id);
    if (!keep) console.warn(`🧹 PROCESSOR-PRUNE: Removing stale entity node ${node.id} (type=${node.type})`);
    return keep;
  });

  if (prunedNodes.length !== updatedNodes.length) {
    console.warn(
      `🧹 PROCESSOR-PRUNE: Pruned ${updatedNodes.length - prunedNodes.length} stale nodes ` +
      `(${updatedNodes.length} → ${prunedNodes.length})`
    );
  }

  return prunedNodes;
}
